import { IdentityApi } from 'api/IdentityApi';
import {
  useFormikHookForOnboardingDetails,
  useGetCustomerOnboardingContactDetails,
} from 'components/shared/onboarding/CustomerOnboarding/CustomerOnboardingContactDetails/hooksCustomerOnboardingContactDetails';
import { OnboardingProfileImageUpload } from 'components/shared/onboarding/UserOnbaording/UserOnboardingDetails/onboardingProfileImageUpload';
import { CustomerOnboardingContactDetailsModel } from 'models/shared/customerOnboarding/customerOnboardingContactDetailsModel';
import { ChangeEvent, MouseEventHandler, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { PlayFill } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import '../customerOnboardingIntro.css';

type PropTypes = {
  nextStep: () => void;
  prevStep: MouseEventHandler;
  invitationToken: string;
};

export const CustomerOnboardingContactDetails = ({
  nextStep,
  prevStep,
  invitationToken,
}: PropTypes): JSX.Element => {
  const [contactDetails, setContactDetails] =
    useState<CustomerOnboardingContactDetailsModel>(
      {} as CustomerOnboardingContactDetailsModel
    );
  const [profileImage, setProfileImage] = useState<string>('');

  const { t } = useTranslation('components');

  useGetCustomerOnboardingContactDetails(setContactDetails, invitationToken);

  const formik = useFormikHookForOnboardingDetails(
    setContactDetails,
    contactDetails,
    invitationToken,
    nextStep,
    t
  );

  const handleProfileImageUpload = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    toast.dismiss();

    if (event.target.files) {
      const selectedFile = event.target.files[0];
      if (selectedFile) {
        if (selectedFile.size > 500 * 1024) {
          toast.error(t('account.err_ProfileImageSizeTooBig'));
          event.target.value = '';
          return;
        }
        const data = new FormData();
        data.append('file', selectedFile);
        const imageLocation = await IdentityApi.updateCustomerProfileImage(
          invitationToken,
          data
        );

        setProfileImage(imageLocation);

        toast.info(t('account.inf_ProfileImageUploaded'));
      }
    }
  };

  return (
    <Card className='onboarding-intro-card'>
      <Card.Body>
        <h4 className='mb-3'>{t('userSettings.lbl_Header')}</h4>
        <Form>
          <Row>
            <Col lg={2} className='text-center'>
              <div className='col col-lg-3 mt-3 text-center'>
                <OnboardingProfileImageUpload
                  profileImage={profileImage}
                  initials={
                    contactDetails.initials ||
                    `${
                      formik.values.firstName !== null &&
                      formik.values.firstName !== undefined
                        ? formik.values.firstName.charAt(0).toUpperCase()
                        : ''
                    }${
                      formik.values.lastName !== null &&
                      formik.values.lastName !== undefined
                        ? formik.values.lastName.charAt(0).toUpperCase()
                        : ''
                    }`
                  }
                  handleImageUpload={handleProfileImageUpload}
                />
              </div>
            </Col>
            <Col className='col col-lg-10'>
              <Row>
                <Col>
                  <Form.Group className='mb-3' controlId='formFirstName'>
                    <Form.Label className='required'>
                      {t('userSettings.lbl_FirstName')}
                    </Form.Label>
                    <Form.Control
                      value={formik.values.firstName || ''}
                      type='input'
                      name='firstName'
                      placeholder={t('userSettings.plh_FirstName')}
                      onChange={formik.handleChange}
                      isInvalid={
                        formik.touched.firstName && !!formik.errors.firstName
                      }
                    />
                    <Form.Control.Feedback type='invalid'>
                      {formik.errors.firstName}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className='mb-3' controlId='formCompany'>
                    <Form.Label className='required'>
                      {t('userSettings.lbl_Company')}
                    </Form.Label>
                    <Form.Control
                      value={formik.values.companyName}
                      type='string'
                      disabled
                    />
                  </Form.Group>
                  <Form.Group className='mb-3' controlId='formEmail'>
                    <Form.Label className='required'>
                      {t('userSettings.lbl_Email')}
                    </Form.Label>
                    <Form.Control
                      value={formik.values.username}
                      type='email'
                      disabled
                    />
                    <Form.Control.Feedback type='invalid'>
                      {formik.errors.username}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className='mb-3' controlId='formLinkedInProfile'>
                    <Form.Label>{t('userSettings.lbl_LinkedIn')}</Form.Label>
                    <Form.Control
                      type='input'
                      name='linkedInProfile'
                      value={formik.values.linkedInProfile || ''}
                      placeholder={t('userSettings.plh_LinkedIn')}
                      onChange={formik.handleChange}
                      isInvalid={
                        formik.touched.linkedInProfile &&
                        !!formik.errors.linkedInProfile
                      }
                    />
                    <Form.Control.Feedback type='invalid'>
                      {formik.errors.linkedInProfile}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className='mb-3' controlId='formLastName'>
                    <Form.Label className='required'>
                      {t('userSettings.lbl_LastName')}
                    </Form.Label>
                    <Form.Control
                      name='lastName'
                      value={formik.values.lastName || ''}
                      type='input'
                      placeholder={t('userSettings.plh_LastName')}
                      onChange={formik.handleChange}
                      isInvalid={
                        formik.touched.lastName && !!formik.errors.lastName
                      }
                    />
                    <Form.Control.Feedback type='invalid'>
                      {formik.errors.lastName}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className='mb-3' controlId='formPosition'>
                    <Form.Label>{t('userSettings.lbl_Position')}</Form.Label>
                    <Form.Control
                      name='position'
                      value={formik.values.position || ''}
                      type='input'
                      placeholder={t('userSettings.plh_Position')}
                      onChange={formik.handleChange}
                      isInvalid={
                        formik.touched.position && !!formik.errors.position
                      }
                    />
                    <Form.Control.Feedback type='invalid'>
                      {formik.errors.position}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className='mb-3' controlId='formPhoneNumber'>
                    <Form.Label>{t('userSettings.lbl_Phone')}</Form.Label>
                    <Form.Control
                      name='phone'
                      value={formik.values.phone || ''}
                      type='input'
                      placeholder={t('userSettings.plh_Phone')}
                      onChange={formik.handleChange}
                      isInvalid={formik.touched.phone && !!formik.errors.phone}
                    />
                    <Form.Control.Feedback type='invalid'>
                      {formik.errors.phone}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
        <Button className='btn btn-eleczap float-start ms-3' onClick={prevStep}>
          <PlayFill className='iconPlayBack' />
          {t('common.btn_Back')}
        </Button>
        <Button
          className='btn btn-eleczap float-end mt-3'
          onClick={formik.handleSubmit}
        >
          {t('common.btn_Next')}
          <PlayFill />
        </Button>
      </Card.Body>
    </Card>
  );
};
